import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { ArrowRight, ChevronLeft, ChevronRight, Cpu, Film, Flame, Layers, Play, Sparkles, Youtube } from "lucide-react";
import { fetchCategories, fetchChannel, fetchVideos, formatDate, formatDuration, formatNumber, listCollections } from "@/lib/api";
import VideoCard from "@/components/VideoCard";
import usePageMeta from "@/hooks/use-page-meta";
import usePageJsonLd from "@/hooks/use-page-jsonld";

export default function Home() {
  const { data: channel } = useSWR("/channel", fetchChannel);
  const { data: cats } = useSWR("/videos/categories", fetchCategories);
  usePageMeta({
    title: "tobiasdeamon Video Archive — Puzzle Games & PC Benchmarks",
    description:
      "The complete tobiasdeamon video archive — 25,000+ uploads indexed by rig, year, and series. PC benchmarks, pet rescue runs, puzzle & word games.",
    image: channel?.banner || channel?.thumbnail,
  });

  // WebSite JSON-LD (enables sitelinks search box in Google).
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  usePageJsonLd({
    key: "website",
    data: origin
      ? {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "tobiasdeamon · Vault",
          url: `${origin}/`,
          description:
            "The complete tobiasdeamon video archive — 25,000+ uploads indexed by rig, year, and series. PC benchmarks, pet rescue runs, puzzle & word games.",
          potentialAction: {
            "@type": "SearchAction",
            target: {
              "@type": "EntryPoint",
              urlTemplate: `${origin}/archive?q={search_term_string}`,
            },
            "query-input": "required name=search_term_string",
          },
        }
      : null,
  });
  const { data: latest } = useSWR(
    ["/videos", { sort: "newest", limit: 9 }],
    () => fetchVideos({ sort: "newest", limit: 9 })
  );
  const { data: popular } = useSWR(
    ["/videos", { sort: "most_viewed", limit: 6 }],
    () => fetchVideos({ sort: "most_viewed", limit: 6 })
  );
  const { data: colsData } = useSWR("/collections", () => listCollections());

  const featuredShelves = useMemo(() => {
    const items = colsData?.items || [];
    // Prefer larger, more meaningful shelves; put YouTube-imported ones first
    return [...items]
      .filter((c) => c.count >= 5)
      .sort((a, b) => {
        const ay = a.source === "youtube" ? 0 : 1;
        const by = b.source === "youtube" ? 0 : 1;
        if (ay !== by) return ay - by;
        return b.count - a.count;
      })
      .slice(0, 12);
  }, [colsData]);

  const featured = latest?.items?.[0];
  const rest = (latest?.items || []).slice(1);

  const marqueeItems = useMemo(() => {
    const items = (latest?.items || []).slice(0, 12).map((v) => v.title);
    return [...items, ...items];
  }, [latest]);

  return (
    <div className="pb-24">
      {/* Ticker */}
      <div className="border-b border-white/10 overflow-hidden py-3 bg-black/40">
        <div className="marquee font-mono-tech text-[11px] uppercase tracking-[0.28em] text-white/40">
          {marqueeItems.map((t, i) => (
            <span key={`${i}-${t}`} className="inline-flex items-center gap-3 whitespace-nowrap">
              <span className="text-[#d95d39]">◆</span> {t}
            </span>
          ))}
        </div>
      </div>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14 pb-10 sm:pb-16">
        <div className="grid grid-cols-12 gap-6 sm:gap-8 items-stretch">
          <div className="col-span-12 lg:col-span-7 space-y-6 sm:space-y-8">
            <div className="mono-label">Personal Video Archive · Est. 2012</div>
            <h1 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#f4f1ea]">
              A vault of every <span className="italic text-[#d95d39]">frame</span> Lukas has
              <br />ever shared with the algorithm.
            </h1>
            <p className="text-white/60 text-base sm:text-lg max-w-2xl leading-relaxed">
              {channel?.description ||
                "PC benchmarks, mobile puzzle marathons, and word-game deep-cuts — catalogued, searchable, and preserved outside the YouTube feed."}
            </p>
            <p
              className="font-serif-editorial italic text-white/50 text-lg sm:text-xl max-w-2xl border-l-2 border-[#d95d39]/60 pl-4"
              data-testid="hero-subline"
            >
              Fourteen years of uploads, indexed by hand — the whole channel, without the algorithm in the way.
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/archive"
                data-testid="hero-browse-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#d95d39] text-white font-mono-tech text-xs uppercase tracking-[0.22em] hover:bg-[#c14e2e] transition-colors"
              >
                Enter the archive <ArrowRight className="w-4 h-4" />
              </Link>
              {featured && (
                <Link
                  to={`/video/${featured.id}`}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 text-[#f4f1ea] font-mono-tech text-xs uppercase tracking-[0.22em] hover:border-[#d95d39] hover:text-[#d95d39] transition-colors"
                >
                  <Play className="w-4 h-4" /> Play latest upload
                </Link>
              )}
            </div>

            {/* Stats banner */}
            <div
              className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden mt-8"
              data-testid="channel-stats-banner"
            >
              {[
                { label: "Uploads", value: formatNumber(channel?.video_count), icon: Film },
                { label: "Subscribers", value: formatNumber(channel?.subscriber_count), icon: Sparkles },
                { label: "Total Views", value: formatNumber(channel?.view_count), icon: Flame },
                { label: "In Archive", value: formatNumber(cats?.total), icon: Cpu },
              ].map((s) => (
                <div key={s.label} className="bg-[#0c0e15] p-5 sm:p-6">
                  <s.icon className="w-4 h-4 text-[#d95d39]" />
                  <div className="mt-4 font-serif-editorial text-3xl sm:text-4xl text-[#f4f1ea]">{s.value || "—"}</div>
                  <div className="mono-label mt-2 text-white/40">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="col-span-12 lg:col-span-5">
            {featured && (
              <Link
                to={`/video/${featured.id}`}
                data-testid="hero-featured-video"
                className="group block relative rounded-2xl overflow-hidden border border-white/10 hover:border-[#d95d39]/60 transition-colors"
              >
                <div className="aspect-video sm:aspect-[3/4] relative">
                  <img
                    src={featured.thumbnail || `https://i.ytimg.com/vi/${featured.id}/hqdefault.jpg`}
                    srcSet={`https://i.ytimg.com/vi/${featured.id}/hqdefault.jpg 480w, https://i.ytimg.com/vi/${featured.id}/maxresdefault.jpg 1280w`}
                    sizes="(max-width: 1024px) 100vw, 40vw"
                    alt={featured.title}
                    width="480"
                    height="360"
                    fetchpriority="high"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#090a0f] via-[#090a0f]/40 to-transparent" />
                  <span className="absolute top-4 left-4 mono-label px-3 py-1 rounded-full bg-black/70 border border-[#d95d39]/40 text-[#d95d39]">
                    Latest Upload
                  </span>
                  <span className="absolute bottom-4 right-4 font-mono-tech text-xs px-2 py-1 rounded bg-black/80 border border-white/10">
                    {formatDuration(featured.duration_seconds)}
                  </span>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="mono-label text-white/50 mb-3">{formatDate(featured.published_at)}</div>
                    <h3 className="font-serif-editorial text-2xl sm:text-3xl leading-tight text-[#f4f1ea] line-clamp-3 group-hover:text-[#d95d39] transition-colors">
                      {featured.title}
                    </h3>
                  </div>
                </div>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* About the archive */}
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 border-y border-white/10"
        data-testid="about-archive-block"
      >
        <div className="grid grid-cols-12 gap-6 sm:gap-8">
          <div className="col-span-12 md:col-span-4">
            <div className="mono-label">Note from the vault</div>
            <h2 className="mt-3 font-serif-editorial text-3xl sm:text-4xl leading-[1.1] tracking-tight text-[#f4f1ea]">
              Why this <span className="italic text-[#d95d39]">archive</span> exists.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-8 space-y-4 text-white/70 text-base sm:text-lg leading-relaxed">
            <p>
              YouTube is a river — good for the algorithm, terrible for finding
              anything you watched a year ago. Fourteen years of uploads get buried
              under whatever the platform surfaces this week.
            </p>
            <p>
              This vault mirrors the whole channel: every PC benchmark, every crostic
              run, every pet-rescue marathon and word-game deep-cut — catalogued by
              hand, tagged, grouped into shelves, and searchable in one place. No
              recommendations. No autoplay. Just the archive, as it was uploaded.
            </p>
          </div>
        </div>
      </section>

      {/* Latest strip */}
      {rest.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
          <div className="flex items-end justify-between mb-6 sm:mb-8">
            <div>
              <div className="mono-label">Section 01</div>
              <h2 className="font-serif-editorial text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#f4f1ea]">Recently Uploaded</h2>
            </div>
            <Link to="/archive" className="mono-label text-white/60 hover:text-[#d95d39] transition-colors">
              See all →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {rest.slice(0, 8).map((v) => (
              <VideoCard key={v.id} video={v} />
            ))}
          </div>
        </section>
      )}

      {/* Popular */}
      {popular?.items?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
          <div className="flex items-end justify-between mb-6 sm:mb-8">
            <div>
              <div className="mono-label">Section 02</div>
              <h2 className="font-serif-editorial text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#f4f1ea]">Most Watched, All Time</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {popular.items.slice(0, 6).map((v) => (
              <VideoCard key={v.id} video={v} size="lg" />
            ))}
          </div>
        </section>
      )}

      {/* Featured Shelves */}
      {featuredShelves.length > 0 && (
        <FeaturedShelves shelves={featuredShelves} />
      )}

      {/* Categories */}
      {cats?.categories?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
          <div className="mb-6 sm:mb-8">
            <div className="mono-label">Section 04</div>
            <h2 className="font-serif-editorial text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#f4f1ea]">Browse by Category</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {cats.categories.map((c) => (
              <Link
                key={c.name}
                to={`/archive?category=${encodeURIComponent(c.name)}`}
                className="group relative rounded-xl p-6 bg-[#12141d] border border-white/10 hover:border-[#d95d39]/60 transition-all hover-lift"
              >
                <div className="mono-label text-white/40 group-hover:text-[#d95d39]">{c.count} videos</div>
                <div className="mt-6 font-serif-editorial text-2xl text-[#f4f1ea] group-hover:text-[#d95d39] transition-colors">
                  {c.name}
                </div>
                <ArrowRight className="absolute bottom-5 right-5 w-4 h-4 text-white/30 group-hover:text-[#d95d39] group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function FeaturedShelves({ shelves }) {
  const scrollerRef = useRef(null);
  const scroll = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-shelf-card]");
    const step = card ? card.getBoundingClientRect().width + 24 : 320;
    el.scrollBy({ left: dir * step * 2, behavior: "smooth" });
  };

  return (
    <section
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16"
      data-testid="featured-shelves"
    >
      <div className="flex items-end justify-between mb-6 sm:mb-8 gap-4 flex-wrap">
        <div>
          <div className="mono-label">Section 03</div>
          <h2 className="font-serif-editorial text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#f4f1ea]">
            Featured Shelves
          </h2>
          <p className="mt-2 text-white/50 max-w-xl text-sm sm:text-base">
            Curated playlists imported straight from YouTube — in the order Lukas queued them.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="shelves-scroll-left"
            onClick={() => scroll(-1)}
            aria-label="Scroll left"
            className="hidden sm:flex w-10 h-10 rounded-full border border-white/10 text-white/60 hover:text-[#d95d39] hover:border-[#d95d39] transition-colors items-center justify-center"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            data-testid="shelves-scroll-right"
            onClick={() => scroll(1)}
            aria-label="Scroll right"
            className="hidden sm:flex w-10 h-10 rounded-full border border-white/10 text-white/60 hover:text-[#d95d39] hover:border-[#d95d39] transition-colors items-center justify-center"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <Link
            to="/collections"
            className="sm:ml-2 mono-label text-white/60 hover:text-[#d95d39] transition-colors"
          >
            All shelves →
          </Link>
        </div>
      </div>

      <div
        ref={scrollerRef}
        data-testid="shelves-scroller"
        className="flex gap-6 overflow-x-auto custom-scrollbar snap-x snap-mandatory pb-4 -mx-4 px-4"
        style={{ scrollBehavior: "smooth" }}
      >
        {shelves.map((c) => (
          <Link
            key={c.slug}
            to={`/collections/${c.slug}`}
            data-shelf-card
            data-testid={`shelf-card-${c.slug}`}
            className="group hover-lift block flex-shrink-0 w-[280px] sm:w-[320px] snap-start rounded-2xl border border-white/10 bg-[#12141d] overflow-hidden"
          >
            <ShelfCover thumbnails={c.preview_thumbnails} />
            <div className="p-5">
              <div className="flex items-center justify-between">
                <span className="mono-label text-white/40">
                  {formatNumber(c.count)} videos
                </span>
                {c.source === "youtube" && (
                  <span
                    title="Imported from YouTube"
                    className="mono-label text-[#d95d39] inline-flex items-center gap-1"
                  >
                    <Youtube className="w-3 h-3" /> YT
                  </span>
                )}
              </div>
              <h3 className="mt-2 font-serif-editorial text-xl text-[#f4f1ea] group-hover:text-[#d95d39] transition-colors line-clamp-2">
                {c.title}
              </h3>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function ShelfCover({ thumbnails = [] }) {
  if (!thumbnails.length) {
    return (
      <div className="aspect-video bg-gradient-to-br from-[#1a1d29] to-[#0c0e15] flex items-center justify-center">
        <Layers className="w-6 h-6 text-white/20" />
      </div>
    );
  }
  if (thumbnails.length === 1) {
    return (
      <div className="aspect-video bg-black">
        <img src={thumbnails[0]} alt="" className="w-full h-full object-cover" loading="lazy" />
      </div>
    );
  }
  const grid = thumbnails.slice(0, 4);
  return (
    <div className="aspect-video grid grid-cols-2 gap-0.5 bg-black">
      {grid.map((t, i) => (
        <div key={i} className="overflow-hidden">
          <img src={t} alt="" className="w-full h-full object-cover" loading="lazy" />
        </div>
      ))}
    </div>
  );
}
